<script setup lang="ts" generic="T, K = never">
import { computed, ref, onUpdated, onMounted } from "vue";

import type { iconName } from "@/Modules/App/Models/Styles/Icons";

import TcIcon from "@/Components/Extend/TcIcon.vue";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

export interface Option<T, K = never> {
  icon?: iconName;
  class?: string;
  label: string;
  value: T;
  extraValue?: K; //Optional prop for additional information
}

export interface TcSelectProps<T, K = never> {
  name: string;
  label?: string;
  placeholder?: string;
  options: Option<T, K>[];
  modelValue?: Option<T, K>;
  isDisabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  maxItemsScroll?: number;
  gutterBottom?: boolean;
  isSmall?: boolean;
  fitContent?: boolean;
  canClear?: boolean;
}

const ITEM_HEIGHT = 56;

const props = withDefaults(defineProps<TcSelectProps<T, K>>(), {
  maxItemsScroll: 5,
  gutterBottom: true,
  isSmall: false,
  fitContent: false,
  canClear: false,
});

const emit = defineEmits<{
  (event: "change", option: Option<T, K> | undefined): void;
  (event: "changeValue", value: T | undefined): void;
  (event: "update:modelValue", option: Option<T, K> | undefined): void;
}>();

const INPUT_WIDTH_DIFF = 22; // Diferencia contando el tamaño del input y las options

const visible = ref<boolean>(false);
const selected = ref<Option<T, K> | undefined>(undefined);
const optionsRef = ref<HTMLUListElement>();
const selectRef = ref<HTMLDivElement>();

const maxHeight = computed(() => props.maxItemsScroll * ITEM_HEIGHT);
const isCloseState = computed(() => props.canClear && hasValue(selected.value) && !visible.value);

const toggle = () => {
  if (!props.isDisabled) {
    if (isCloseState.value) {
      selected.value = undefined;

      select(undefined);
    } else {
      visible.value = !visible.value;
    }
  }
};

const select = (option: Option<T, K> | undefined) => {
  emit("change", option);
  emit("changeValue", option?.value);
  emit("update:modelValue", option);

  selected.value = option;
};

const onClickOut = () => {
  visible.value = false;
};

const updateMaxWidthItem = () => {
  if (!props.fitContent) return;

  const maxItemWidth = optionsRef.value!.clientWidth + INPUT_WIDTH_DIFF + 4;

  selectRef.value!.style.width = `${maxItemWidth}px`;
  optionsRef.value!.style.width = `${maxItemWidth}px`;
};

onMounted(() => {
  if (hasValue(props.modelValue) && Array.isArray(props.options)) {
    const candidate = props.options.find((op: Option<T, K>) => op.value === props.modelValue?.value);

    if (hasValue(candidate)) {
      selected.value = candidate;
    }
  }

  updateMaxWidthItem();
});

onUpdated(() => {
  if (
    hasValue(selected.value) &&
    props.options.findIndex((o: Option<T, K>) => o.value === selected.value?.value) === -1
  ) {
    selected.value = undefined;
  }

  if (
    hasValue(props.modelValue) &&
    (!hasValue(selected.value) || props.modelValue.value !== selected.value)
  ) {
    const candidate = props.options.find((op: Option<T, K>) => op.value === props.modelValue?.value);

    if (hasValue(candidate)) {
      selected.value = candidate;
    }
  }
});
</script>

<template>
  <div
    ref="selectRef"
    class="tc-select"
    :class="{
      'tc-select--disabled': props.isDisabled,
      'mb-4': props.gutterBottom,
      'tc-select--small': props.isSmall,
      'tc-select--fit-width-options': props.fitContent,
      'tc-select--open': visible,
    }"
  >
    <div v-if="visible" class="tc-select__click_cover" @click="onClickOut" />

    <label v-if="isValidString(props.label)" class="tc-select__label">
      {{ props.label }}
    </label>

    <div
      class="tc-select__selector"
      :class="{
        'tc-select__selector--error': props.error,
        'tc-select__selector--visible': visible,
        'tc-select__selector--expanded': visible,
      }"
      @click="toggle"
    >
      <div v-if="!hasValue(selected)" class="tc-select__selector__placeholder">
        <span v-if="isValidString(props.placeholder)">{{ props.placeholder }}</span>
        <span v-else>&nbsp;</span>
      </div>

      <div v-if="hasValue(selected)" class="tc-select__selector__value">
        <span>{{ selected.label }}</span>
      </div>

      <TcIcon
        v-if="!isCloseState"
        name="ChevronDown"
        :color="visible ? 'content-high' : 'content-mid'"
        class="tc-select__selector__arrow"
        size="md"
      />
      <TcIcon v-else name="Cross" color="secondary-high" class="tc-select__selector__arrow" />

      <ul ref="optionsRef" class="tc-select__selector__list" :style="'max-height:' + maxHeight + 'px'">
        <li
          v-for="(option, index) in props.options"
          class="tc-select__selector__list__element"
          :key="`selector-${props.name}-${index}`"
          :class="{
            'tc-select__selector__list__element--current':
              hasValue(selected) && option.value === selected.value,
            ...(isValidString(option.class) ? { [option.class]: true } : {}),
          }"
          @click="select(option)"
        >
          <div>
            <TcIcon
              v-if="isValidString(option.icon)"
              class="tc-select__selector__list__element__icon"
              color="secondary-high"
              size="sm"
              :name="option.icon as iconName"
            />

            <p class="tc-select__selector__list__element__label">
              {{ option.label }}
            </p>
          </div>
        </li>
      </ul>

      <Teleport to="#app" v-if="visible">
        <div class="tc-select__click_cover tc-select__click_cover--main" @click="onClickOut" />
      </Teleport>
    </div>

    <span v-if="props.error" class="tc-select__error">{{ props.errorMessage }}</span>
  </div>
</template>
<style scoped lang="scss">
.tc-select {
  cursor: pointer;
  display: flex;
  flex-direction: column;

  &__click_cover {
    background-color: transparent;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }

  &__label {
    @include text.general(low, m);
    @include colors.color(content, high);
    @include sp.margin(b, 2);

    display: block;

    &:first-letter {
      text-transform: uppercase;
    }
  }

  &__error {
    @include text.general(low, s);
    @include colors.color(semantic, error);
  }

  &__selector {
    @include radius(s);
    @include border(1, solid);
    @include colors.border(content, low);
    @include colors.bg(background, base);

    position: relative;
    display: flex;
    width: 100%;
    height: $size-input-height;

    &__arrow {
      position: absolute;
      right: $space-4;
      top: 50%;
      transform: rotateZ(0deg) translateY(-50%);
      transition-duration: 0.4s;
      transition-timing-function: cubic-bezier(0.59, 1.39, 0.37, 1.01);
    }

    &__placeholder,
    &__value {
      @include text.general(low, m);
      @include sp.padding(y, 3);
      @include sp.padding(l, 4);
      @include sp.padding(r, 12);

      display: flex;
      width: 100%;
      align-items: center;

      span {
        &::first-letter {
          text-transform: uppercase;
        }
      }
    }

    &__value {
      @include colors.color(content, high);
    }

    &__placeholder {
      @include colors.color(content, mid);
    }

    &__list {
      @include colors.bg(background, base);
      @include sp.padding(y, 0);
      @include sp.padding(x, 5);

      @include border(1, solid);
      @include colors.border(content, high);
      @include utils.noScrollbar();

      width: calc(100% + 2px);
      list-style-type: none;
      position: absolute;
      left: -1px; // es para que si tiene un input detrás las opciones no se vea el bordeado
      top: 100%;
      // fallback if it's a max-height computed error
      max-height: 256px;
      overflow-y: scroll;
      z-index: 1001;
      display: none;

      &__element {
        @include text.general(low, m);
        @include colors.color(content, mid);
        @include sp.padding(y, 2);

        & > div {
          @include text.modification(left);

          display: flex;
          align-items: center;
        }

        &__icon {
          @include sp.margin(r, 2);

          flex-grow: 0;
        }

        &__label {
          @include text.general(low, m);
          @include colors.color(content, mid);

          flex-grow: 1;
        }

        &:hover,
        &--current {
          .tc-select__selector__list__element {
            &__label {
              @include colors.color(content, high);
            }
          }
        }
      }
    }

    &--visible {
      @include border(1, solid);
      @include colors.border(content, high);

      .tc-select__selector__list {
        display: block;
      }
    }

    &--error {
      @include border(1, solid);
      @include colors.border(semantic, error);
    }

    &--expanded {
      .tc-select__selector {
        &__arrow {
          transform: rotateZ(180deg) translateY(50%);
        }
      }
    }
  }

  &--small {
    .tc-select__selector__value {
      @include sp.padding(y, 2, true);
    }
  }

  &--disabled {
    opacity: 40%;
    cursor: not-allowed;
    pointer-events: none;
  }

  &--fit-width-options {
    .tc-select__selector__list {
      width: max-content;
      opacity: 0;
      display: block;
      pointer-events: none;
    }
  }

  &--open {
    .tc-select__selector {
      border-bottom-left-radius: 0px !important;
      border-bottom-right-radius: 0px !important;
      border-bottom: 0px;

      &__list {
        @include radius(m);

        border-top-left-radius: 0px !important;
        border-top-right-radius: 0px !important;
        border-top: 0px;
      }
    }

    &.tc-select--fit-width-options {
      .tc-select__selector__list {
        opacity: 1;
        pointer-events: all;
      }
    }
  }

  .hidden {
    visibility: hidden;
  }

  .visible {
    visibility: visible;
  }
}
</style>
